import { useTranslation } from "@next-platform/client-common-core";
import { type RulebookDraft } from "@next-platform/client-common-core/rulebook/editor/rulebookDraftStore";
import { type RulebookSummary } from "@next-platform/packages-ts-types";
import React, { memo, useMemo } from "react";

import ConfirmationModal from "@/components/common/ConfirmationModal";
import Trans from "@/components/Trans/Trans";
import RulebookListCard from "@/pages/RulebookMy/RulebookListCard";
import buildMyRulebookCardFields from "@/pages/RulebookMy/utils/buildMyRulebookCardFields";

import useMyRulebookCardActions from "./hooks/useMyRulebookCardActions";

type MyRulebookCardProps = {
  /** The saved Rulebook this card stands for; absent for a local-only draft. */
  summary?: RulebookSummary;
  /** The local draft for this row — a draft-only row's own, or the draft linked to `summary`. */
  draft?: RulebookDraft;
  /** Opens a draft in the Editor by id. */
  onOpen: (draftId: string) => void;
  /** Called after a successful import or delete so the saved list refreshes. */
  onReloadSaved?: () => void;
  "data-testid"?: string;
};

/**
 * One My Rulebooks card: a saved Rulebook's summary or a local-only draft.
 */
const MyRulebookCard = ({ summary, draft, onOpen, onReloadSaved, "data-testid": testId = "MyRulebookCard" }: MyRulebookCardProps) => {
  const { t } = useTranslation();
  const { menuActions, open, draftDeletion, savedDelete } = useMyRulebookCardActions({
    ...(summary ? { summary } : {}),
    ...(draft ? { draft } : {}),
    onOpen,
    onReloadSaved,
  });
  const cardFields = useMemo(() => buildMyRulebookCardFields({ ...(summary ? { summary } : {}), ...(draft ? { draft } : {}) }), [summary, draft]);
  const name = summary?.name ?? draft?.definition.name ?? "";
  const savedDeleteValues = useMemo(() => ({ name }), [name]);
  const savedDeleteMessage = useMemo(
    () => <Trans i18nKey="rulebookEditor:card.deleteRulebookMessage" values={savedDeleteValues} />,
    [savedDeleteValues],
  );

  return (
    <>
      <RulebookListCard
        id={summary?.id ?? draft?.definition.id ?? ""}
        {...cardFields}
        mode="owner"
        menuActions={menuActions}
        onOpen={open}
        data-testid={testId}
      />
      {draft ? (
        <ConfirmationModal
          isVisible={draftDeletion.isOpen}
          heading={draftDeletion.heading}
          confirmationMessage={draftDeletion.message}
          handleClose={draftDeletion.close}
          onUserConfirmation={draftDeletion.confirm}
          isLoading={draftDeletion.isDeleting}
          dataTestId="RulebookDraftCard-DeleteModal"
        />
      ) : null}
      {summary && !draft ? (
        <ConfirmationModal
          isVisible={savedDelete.isOpen}
          heading={t("rulebookEditor:card.deleteRulebookHeading")}
          confirmationMessage={savedDeleteMessage}
          handleClose={savedDelete.close}
          onUserConfirmation={savedDelete.confirm}
          isLoading={savedDelete.isDeleting}
          dataTestId="RulebookSavedCard-DeleteModal"
        />
      ) : null}
    </>
  );
};

export default memo(MyRulebookCard);
